import { useEffect, useState } from "react";
import { api, formatError, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink } from "lucide-react";

function ImagePick({ label, path, onPicked, testid }) {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onPicked(data.path); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-3 mt-1">
        {path ? <img src={imageUrl(path)} alt="" className="h-14 w-14 object-contain rounded-lg border bg-white" /> : <div className="h-14 w-14 rounded-lg border border-dashed grid place-items-center text-[10px] text-slate-400">None</div>}
        <Input type="file" accept="image/*" onChange={pick} data-testid={testid} />
        {path && <Button size="sm" variant="ghost" onClick={() => onPicked(null)}>Clear</Button>}
      </div>
    </div>
  );
}

export default function BrandingForm({ slug, name }) {
  const [b, setB] = useState({ logo_path: null, hero_path: null, welcome: "", contact_name: "", contact_email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/regions/${slug}`).then((r) => setB({ ...r.data, welcome: r.data.welcome || "", contact_name: r.data.contact_name || "", contact_email: r.data.contact_email || "" })).catch(() => {}); }, [slug]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/staff/regions/${slug}/branding`, { ...b, contact_email: b.contact_email || null }); toast.success("Regional branding saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid="branding-card">
      <CardHeader>
        <CardTitle className="font-display text-xl">{name} branding</CardTitle>
        <CardDescription>Shown on {slug}.markdowns.nz. Layout and prices stay the same across all regions.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ImagePick label="Regional logo" path={b.logo_path} onPicked={(p) => setB({ ...b, logo_path: p })} testid="branding-logo-input" />
        <ImagePick label="Hero photo (optional)" path={b.hero_path} onPicked={(p) => setB({ ...b, hero_path: p })} testid="branding-hero-input" />
        <div><Label>Welcome line</Label><Input maxLength={140} value={b.welcome} onChange={(e) => setB({ ...b, welcome: e.target.value })} placeholder={`${name}'s best local markdowns`} data-testid="branding-welcome" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Contact name</Label><Input value={b.contact_name} onChange={(e) => setB({ ...b, contact_name: e.target.value })} data-testid="branding-contact-name" /></div>
          <div><Label>Contact email</Label><Input type="email" value={b.contact_email} onChange={(e) => setB({ ...b, contact_email: e.target.value })} data-testid="branding-contact-email" /></div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="branding-save">{busy ? "Saving…" : "Save branding"}</Button>
          <a href={`/?site=${slug}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="branding-preview"><ExternalLink className="w-4 h-4 mr-1" />Preview site</Button></a>
        </div>
      </CardContent>
    </Card>
  );
}
